import { getInitialState, reducer, StepConfig, StepControlsState } from "./reducer/reducer"
import {
    Action,
    advanceStepper,
    resetStepper,
    reverseStepper,
    updateNextStep,
    updateBackStep
} from "./reducer/actions"
import React, {
    createContext,
    FunctionComponent,
    useCallback,
    useContext,
    useMemo,
    useReducer
} from "react"

export interface StepControlsContextProps {
    state: StepControlsState
    show: (step: Step) => void
    hide: (step: Step) => void
    activate: (step: Step) => void
    deactivate: (step: Step) => void
    navigateNext: () => void
    navigateBack: () => void
    reset: () => void
}

export const StepControlsContext = createContext<StepControlsContextProps>({
    state: getInitialState(),
    show: (step: Step) => {},
    hide: (step: Step) => {},
    activate: (step: Step) => {},
    deactivate: (step: Step) => {},
    navigateNext: () => {},
    navigateBack: () => {},
    reset: () => {}
})

interface StepControlsProviderProps {
    value?: StepControlsContextProps
    children: React.ReactNode
}

export enum Step {
    NEXT,
    BACK
}

export const StepControlsProvider: FunctionComponent<StepControlsProviderProps> = props => {
    const [state, dispatch] = useReducer(reducer, getInitialState())

    const updateControl = useCallback(
        (step: Step, config: Partial<StepConfig>) => {
            switch (step) {
                case Step.BACK:
                    return dispatch(updateBackStep(config))
                case Step.NEXT:
                    return dispatch(updateNextStep(config))
                default:
                    throw new Error(`Invalid step value [${step}] provided`)
            }
        },
        [state, dispatch]
    )

    const show = useCallback(
        (step: Step) => {
            updateControl(step, { visible: true })
        },
        [updateControl]
    )

    const hide = useCallback(
        (step: Step) => {
            updateControl(step, { visible: false })
        },
        [updateControl]
    )

    const activate = useCallback(
        (step: Step) => {
            updateControl(step, { active: true })
        },
        [updateControl]
    )

    const deactivate = useCallback(
        (step: Step) => {
            updateControl(step, { active: false })
        },
        [updateControl]
    )

    const navigateNext = useCallback(() => {
        dispatch(advanceStepper())
    }, [dispatch])

    const navigateBack = useCallback(() => {
        dispatch(reverseStepper())
    }, [dispatch])

    const reset = useCallback(() => {
        dispatch(resetStepper())
    }, [dispatch])

    const stepControlsContextValue = useMemo(
        () => ({
            state,
            show,
            hide,
            activate,
            deactivate,
            navigateNext,
            navigateBack,
            reset
        }),
        [state, show, hide, activate, deactivate, navigateNext, navigateBack, reset]
    )

    return (
        <StepControlsContext.Provider value={props.value ?? stepControlsContextValue} {...props} />
    )
}

export const useStepControlsContext = () => {
    const context = useContext(StepControlsContext)
    if (!context)
        throw new Error("useStepControlsContext must be used within a StepControlsProvider")
    return context
}
